import React, { 
  FC, forwardRef, MouseEvent, ReactNode, useEffect, useImperativeHandle, useReducer, useRef, useCallback, 
} from 'react';
// import shallowCompare from 'react-addons-shallow-compare';
import { useDispatch, useSelector } from 'react-redux';
import { List as ImList, Set as ImSet, Map as ImmutableMap } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import noop from 'lodash/noop';
import { usePrevious } from 'react-use';

import { makeStyles } from 'tss-react/mui';

import type RootState from 'companion-app-components/utils/redux-store/rootState';
import type { Attachment, CashFlowTransaction } from 'companion-app-components/flux/transactions/transactionsTypes';
import { getLogger, tracker } from 'companion-app-components/utils/core';
import { accountsActions, accountsTypes, accountsUtils, accountsSelectors } from 'companion-app-components/flux/accounts';
import { tagsSelectors } from 'companion-app-components/flux/tags';
import { featureFlagsSelectors } from 'companion-app-components/flux/feature-flags';
import { authSelectors } from 'companion-app-components/flux/auth';
import { transactionsActions, transactionsTypes, transactionsTransformers, transactionsUtils } from 'companion-app-components/flux/transactions';
import { scheduledTransactionsActions, scheduledTransactionsSelectors } from 'companion-app-components/flux/scheduled-transactions';
import { makeMemorizedRuleFromTransactionAction } from 'companion-app-components/flux/memorized-rules/memorizedRulesActions';
import * as documentActions from 'companion-app-components/flux/documents/documentsActions';
import { rootUiActions, rootUiTypes } from 'companion-app-components/flux/root-ui';
import { isAccountGroupEnabled } from 'companion-app-components/flux/account-groups/accountGroupsSelectors';
import { getDatasetAccessControlData } from 'companion-app-components/flux/account-groups/accountGroupUtils';
import { datasetsSelectors } from 'companion-app-components/flux/datasets';

import * as transactionsSpecialActions from 'data/transactions/actions';
import {
  isUnbalancedSplitTxn, balanceSplitTxn,
  isSupportedTransferTxn, isUnacceptedScheduledTxn, 
  isActionableScheduledInstance, updateNewTags, isExistingTxn, isBankOwnedPending, isUnAcceptedTxnType,
  updateMemorizePayeeValues,
} from 'data/transactions/utils';
import * as transactionListSelectors from 'data/transactionList/selectors';
import * as transactionSelectors from 'data/transactions/selectors';
import { TxnPendingStates } from 'data/transactions/types';
import * as payeeSelectors from 'data/payees/selectors';
import { isC2REnabledAccount } from 'data/accounts/utils';

import useQDialogs from 'components/QDialogs/useQDialogs';
import useQPreferences from 'components/QPreferences/useQPreferences';
import { showBulkEditConfirmDialog } from 'components/Dialogs/BulkEditConfirmDialog/actions';
import { showDeleteReminderDialog } from 'components/Dialogs/DeleteReminderDialog/actions';
import { showMatchTxnDialog } from 'components/Dialogs/MatchTxnDialog/actions';
import { DIALOG_TYPE_MEMORIZED_RULE_EDIT } from 'components/Dialogs/MemorizedRuleEditDialog';
import { copyObject, normalizeAmt, addEventListenerOnce } from 'utils/utils';
import useQData from 'components/QData/useQData';
import { useUIState } from 'hooks/useUIState';
import TransactionPage from './TransactionPage';
import * as helpers from './helpers';
import {
  newRegFields,
  txnListHasAttachment,
  firstBankAccount,
  isTransferAccountAvailable,
} from './utils';
import { QUEUE_PAYMENTS } from './TransactionRow2/utils';
import { initShowTransactions, RegisterUIStateType, uiStateConfig } from './uiState';
import { styles } from './styles';

const log = getLogger('components/TransactionRegister/index.tsx');
const useStyles = makeStyles()(styles as Record<string, any>);

export interface TransactionRegisterProps {
  accountIds: ImList<number>;  // Precisely, an immutable OrderedMap or empty list for all accounts
  accountsById: Record<string, any>;
  accountNodeId: string | number;
  autoSave: boolean;
  continuousCreate: boolean;
  overrideTxns: CashFlowTransaction[]; // if provided, a set of transactions to display, bypassing retrieval from accounts
  navFn: (accountId: string, txnId: string) => void;  // called on "go to transfer"
  showControls: boolean;
  tightMargins: boolean;
  noNew: boolean;
  hideSearch: boolean;
  scrollToId: string; // ID of transaction to scroll to
  getRef?: (component: ReactNode) => void;
  wrapperId: string;
  filterFunction?: () => void;
  hideDividers: boolean;
  openPrefs?: () => void;
  sortBy: string;
  sortOrder: string;
  onSortChange: (sortField: string, sortOrder: string) => void;
  reminderSetting: string | number,
  calendar: boolean;
  earliestTransactionDate: string;
  calendarDate: Record<string, any>,
  setCalendarDate: (year: string, month: string) => void;
  zeroStateMessage: string;
  regFieldsOrder: ImList<string>;
  regFieldsOrderChange?: (list: any) => void;
  deleteDownloaded?: boolean;
  showCurrencySymbol: boolean;
  showDebtErrorMessage: boolean;
  doubleAmounts?: boolean;
  onTxnUpdate: () => void;
  // Refs
  addNewTransactionRef: (cb: () => void) => void;
}

const TransactionRegister: FC<TransactionRegisterProps> = forwardRef((props: TransactionRegisterProps, ref) => {  
  const { uiState, setUIState } = useUIState(props, uiStateConfig);
  const { datasetPreferences, setDatasetPreference } = useQPreferences();
  const {
    qDataSaveTransactions,
    qDataDeleteTransactions,
    qDataMatchTransactions,
    qDataUnMatchTransaction,
    qDataPerformTransactionAction,
  } = useQData();

  const { classes } = useStyles();
  const { dialogAlert } = useQDialogs();
  const [, forceUpdate] = useReducer((val) => val + 1, 0);
  const currentFocusField = useRef<null | HTMLElement>(null);
  const {
    calendarDate,
    accountIds = ImList(),
    calendar = false,
    zeroStateMessage = null,
    ...otherProps
  } = props;

  const { filterObject, searchBoxFilter } = uiState;
  const wrappedFilterFn = helpers.createFilterFromObjectAndExpression(filterObject, searchBoxFilter);
  let sortBy = 'date';
  let sortOrder = 'descending';
  let reminderSetting: string | number = 0;
  let nodePref: null | Record<string, any> = null;
  if (datasetPreferences) {
    nodePref = datasetPreferences.accountNodePrefs.get(props.accountNodeId);
  }
  // Reminder Setting
  if (props.accountsById && accountIds && accountIds.size <= 1) {
    if (props.reminderSetting) {
      reminderSetting = props.reminderSetting;
    } else {
      const acctRec = props.accountsById.get(accountIds.first());

      reminderSetting = accountsUtils.getReminderSettingInDays(acctRec?.recurringTxn);
    }
  } else {
    reminderSetting = props.reminderSetting || 'account';  // use each accounts reminder setting preference to determine the list
  }
  // For acme, we actually read the pref from the global location for sort
  if (nodePref) {
    sortBy = nodePref.sortBy || sortBy;
    sortOrder = nodePref.sortOrder || sortOrder;
  }
  // all overridden if props are passed in
  sortBy = props.sortBy || sortBy;
  sortOrder = props.sortOrder || sortOrder;

  // Selectors
  const datasetsLoadPending = useSelector((state: RootState) => datasetsSelectors.getLoadPending(state));
  const featureFlags = useSelector((state: RootState) => featureFlagsSelectors.getFeatureFlags(state));
  const txnsByAccountId = useSelector((state: RootState) => transactionSelectors.getTransactionsByAccountId(state, null));
  const txnsById = useSelector((state: RootState) => transactionSelectors.getTransactionsById(state));
  const calendarTxns = useSelector(() => props.overrideTxns && transactionListSelectors.processCalendarTxns(props.overrideTxns));
  const transactionsWithCategorySuggestions = useSelector((state: RootState) =>
    transactionListSelectors.getTransactionsWithCategorySuggestions(state, { accountIds }));
  const payeesForAccounts = useSelector((state: RootState) =>
    payeeSelectors.getPayeesForAccounts(state, { accountIds }));
  const tags = useSelector((state: RootState) => tagsSelectors.getTags(state));
  const scheduledTransactionsById = useSelector((state: RootState) =>
    scheduledTransactionsSelectors.getScheduledTransactions(state));
  const scheduledTransactionsLoadPending = useSelector((state: RootState) =>
    scheduledTransactionsSelectors.getLoadPending(state));
  const allAccountIds = useSelector((state: RootState) => accountsSelectors.getAllAccountIds(state));
  const dataset = useSelector((state: RootState) => authSelectors.getCurrentDataset(state));
  const isMacDataset = useSelector((state: RootState) => authSelectors.getIsMacDataset(state));
  const isEntityGroupEnabledUser = useSelector(isAccountGroupEnabled);
  const entityAccountsById = useSelector(accountsSelectors.getWriteEntityShareAccountsById);
  const { isEntityShare } = getDatasetAccessControlData(dataset);
  const hideScheduledTransactionsInRegister = Boolean(
    datasetsLoadPending || !dataset?.id || !dataset.entitlements || isEntityShare,
  );
  const autoGetScheduledTransactionRef = useRef(false);

  const txnListForAccountIds = useSelector((state: RootState) => transactionListSelectors.getTransactionListForAccountIds(state, {
    id: props.wrapperId,
    accountIds,
    sortBy,
    sortOrder,
    filterFn: wrappedFilterFn,
    filterExpression: props.filterFunction ? undefined : searchBoxFilter,
    overrideTxns: props.overrideTxns,
    reminderSetting: hideScheduledTransactionsInRegister ? 0 : reminderSetting,
    earliestTransactionDate: props.earliestTransactionDate,
  }));
  const acctTxns = calendar ? calendarTxns : txnListForAccountIds;

  // Feature Flags
  const txnCancelExtraColumn = featureFlags.get('txnCancelExtraColumn');
  const hideLoanTransactions = featureFlags.get('hideLoanTransactions');
  const hideConnectedLoanTransactions = featureFlags.get('hideConnectedLoanTransactions');

  // Dispatch events
  const dispatch = useDispatch();
  const getTransactions = () => dispatch(transactionsActions.getTransactions(undefined, { context: 'register' }));
  const deleteDocument = (data) => dispatch(documentActions.deleteDocument(data));
  const updateAccount = (data) => dispatch(accountsActions.updateAccount(data));
  const deleteScheduledTransaction = (data) =>
    dispatch(scheduledTransactionsActions.deleteScheduledTransaction(data, { context: 'register' }));
  const dispatchDeleteReminderDialog = (txn) => dispatch(showDeleteReminderDialog(txn));
  const updateTransactionCategoriesForPayeeAction = (data) =>
    dispatch(transactionsSpecialActions.updateTransactionCategoriesForPayeeAction(data, { context: 'register' }));
  const showRootDialog = (dialogData) => dispatch(rootUiActions.createDialog(rootUiTypes.mkRootUiData(dialogData)));
  const dispatchShowMatchTxnDialog = (txn) => dispatch(showMatchTxnDialog(txn));
  const makeMemorizedRuleFromTransaction = (txn) => dispatch(makeMemorizedRuleFromTransactionAction(txn));

  const isTransferAccountAvailableFn = useCallback((txn) => isTransferAccountAvailable(txn, isEntityGroupEnabledUser, entityAccountsById), [isEntityGroupEnabledUser, entityAccountsById]);

  const findTxnsForIds = (ids: string[], isVisible: boolean = true): Record<string, any>[] => {
    const txnIds = ImSet(ids);
    let txnList = [];
    log.log('Find Transaction ', txnIds.toJS());
    acctTxns.forEach((section, key) => {
      const shortKey = key.slice(key.indexOf(':') + 1);
      if (!isVisible || uiState.openSections?.has(shortKey) || props.hideDividers) {
        const txnArray = section.reduce((list, txn) => {
          if (txnIds.has(txn.id)) {
            list.push({ txn, shortKey });
          }
          return list;
        }, []);
        txnList = txnList.concat(txnArray);
      }
    });

    return txnList;
  };

  const clearTxnBeingEdited = (stateObj?: Partial<RegisterUIStateType>) => {
    const { selectedTxns, txnBeingEdited } = uiState;
    if (txnBeingEdited) {
      if (selectedTxns?.has(txnBeingEdited)) {
        const txnsFound = findTxnsForIds(txnBeingEdited.id ? [txnBeingEdited.id] : []);
        if (txnsFound && txnsFound.length > 0) {
          const newSelectedTxns = selectedTxns.remove(txnBeingEdited).add(txnsFound[0].txn);
          if (stateObj) {
            return { ...stateObj, txnBeingEdited: null, selectedTxns: newSelectedTxns };
          }
          return setUIState({ txnBeingEdited: null, selectedTxns: newSelectedTxns });
        }
      }
    }
    return { ...stateObj, txnBeingEdited: null };
  };

  const cancelEdit = (addState = {}, passive = false): RegisterUIStateType => {
    const stateObj = clearTxnBeingEdited({
      showNewTxn: false,
      simpleEdit: true,
      showSplitWindow: false,
      currEditFieldName: 'payee',
      scrollToId: null,
      txnDirty: false,
      ...addState,
    });

    currentFocusField.current = null;
    return passive ? stateObj : setUIState(stateObj);
  };

  const setTxnReviewed = (txn) => {
    if (txn && uiState.txnBeingEdited !== txn && !txn.isReviewed) {
      return txn.set('isReviewed', true);
    }
    return txn;
  };

  const setTxnBeingEdited = (txn: CashFlowTransaction, stateObj?: Partial<RegisterUIStateType>) => {
    const { selectedTxns } = uiState;
    if (uiState.txnBeingEdited && selectedTxns?.has(uiState.txnBeingEdited)) {
      if (stateObj) {
        return { ...stateObj, txnBeingEdited: txn, selectedTxns: selectedTxns.add(txn) };
      }
      return setUIState({
        txnBeingEdited: txn,
        selectedTxns: selectedTxns.remove(uiState.txnBeingEdited).add(txn),
      });
    }
    return { ...stateObj, txnBeingEdited: txn };
  };

  const setTxnUserOwnedBankPending = (txn) => txn.set('source', TxnPendingStates.USER_OWNED_BANK_PENDING);

  const saveTransaction = (
    savingTxn: CashFlowTransaction,
    cb: any = false,
    overwriteReview = true,
  ) => {
    // log.debug('_SAVING TX', txn);
    let txn = overwriteReview ? setTxnReviewed(savingTxn) : savingTxn;
    txn = txn.source === TxnPendingStates.QCS_REFRESH_BANK_PENDING ? setTxnUserOwnedBankPending(txn) : txn;
    const txnToSave = txn; // .set('amount', normalizeAmt(txn.amount));
    qDataSaveTransactions(
      [txnToSave],
      (saved) => {
        if (cb) {
          cb(saved);
        }
      },
    );
  };

  const quickSaveTransaction = (
    txnToSave: CashFlowTransaction,
    _forceDirty: boolean = false,
    _doCancelEdit: boolean = false,
  ) => {
    const txn = setTxnReviewed(txnToSave);
    setUIState(setTxnBeingEdited(txn, { txnDirty: true }));
  };

  const setCalendarDate = (year: string, month: string) => {
    if (props.setCalendarDate) {
      props.setCalendarDate(year, month);
    }
  };


  //  Primarily used to remove filters when returning to the transaction page from another page
  const clearAllFilters = (passedProps: Partial<TransactionRegisterProps>) => {
    setUIState({
      filterObject: null,
      searchBoxFilter: '',
      ...(acctTxns?.size > 0 && uiState.openSections?.size === 0 ?
        { openSections: initShowTransactions({ ...passedProps, acctTxns }, 300) } : {}
      ),
    });
  };

  const findTxnById = (id: string): undefined | Record<string, any> => {
    let txnList = [];
    acctTxns.forEach((section) => {
      const txnArray = section.reduce((list, txn) => {
        if (txn.id === id) {
          list.push({ txn });
        }
        return list;
      }, []);
      txnList = txnList.concat(txnArray);
    });

    return txnList.length ? txnList[0] : undefined;
  };

  const alterHeader = (newList) => {
    log.log('Alter Header', newList);
    const listRegFields = props.regFieldsOrder;
    if (JSON.stringify(listRegFields.toJS()) !== JSON.stringify(newList.toJS())) {
      if (props.regFieldsOrderChange) {
        props.regFieldsOrderChange(newList);
      }
    }
  };

  const scrollToTransaction = (txnId) => {
    setUIState({ scrollToId: txnId });
  };

  const refreshTxnList = () => {
    getTransactions();
  };

  const resetFocus = () => {
    if (uiState.simpleEdit && !uiState.showSplitWindow) {
      setTimeout(() => {
        if (currentFocusField.current && uiState.simpleEdit) {
          currentFocusField.current.focus();
        }
      }, 100);
    }
  };

  useImperativeHandle(ref, () => ({
    resetFocus,
    scrollToTransaction,
  }));

  const onFocusField = (e) => {
    currentFocusField.current = e;
  };

  const animationControl = (field: string) => {
    setUIState({ [field]: null });
  };

  const onSearchFilter = (currentSearchBoxFilter) => {
    if (!currentSearchBoxFilter && searchBoxFilter) {
      setUIState({ searchBoxFilter: currentSearchBoxFilter });
    } else if (currentSearchBoxFilter && currentSearchBoxFilter.trim().length > 0) {
      setUIState({ showNewTxn: false, txnBeingEdited: null, searchBoxFilter: currentSearchBoxFilter });
    }
  };

  const onApplyFilter = (newFilterObject) => {
    setUIState({ filterObject: newFilterObject });
  };

  // download a CSV of the current contents of the tx list
  const downloadFile = () => {
    helpers.downloadRegisterToCSV({ ...props, acctTxns, dataset, uiState });
  };

  const onRowSelect = (txn, isSet) => {
    let newSelectedTxns;
    const { selectedTxns } = uiState;
    if (isSet) {
      newSelectedTxns = selectedTxns?.withMutations((mutable) => {
        mutable.add(txn);
      });
    } else {
      newSelectedTxns = selectedTxns?.withMutations((mutable) => {
        mutable.remove(selectedTxns.find((t) => t.id === txn.id));
      });
    }
    const totalTxnsCount = acctTxns.keySeq().reduce((aggCount, key) =>
      aggCount + acctTxns.get(key).size, 0);
    setUIState({
      allSelected: newSelectedTxns.size === totalTxnsCount,
      selectedTxns: newSelectedTxns,
    });

    resetFocus();
  };

  const onCheckAll = (on) => {
    if (!on) {
      setUIState({ allSelected: false, selectedTxns: uiState.selectedTxns?.clear() });
    } else {
      const selectedTxns = uiState.selectedTxns?.withMutations((mutable) => {

        acctTxns.forEach((section, key) => {
          const shortKey = key.slice(key.indexOf(':') + 1);
          if (uiState.openSections?.has(shortKey) || props.hideDividers) {
            section.forEach((txn) => {
              const isTypeGrossPaycheck = transactionsUtils.isTxnGrossPaycheckType(
                txn,
                txnsByAccountId,
                scheduledTransactionsById,
              );
              const isQueuePmt = QUEUE_PAYMENTS.includes(txn.check?.number);
              const isAccountSynced = !(txn.id && txn?.coa?.type === 'ACCOUNT' && !allAccountIds.includes(txn.coa.id));
              if (
                !isTypeGrossPaycheck
                && !isQueuePmt
                && isAccountSynced
                && (!isC2REnabledAccount(txn.accountId) || !isUnAcceptedTxnType(txn))
                && isTransferAccountAvailableFn(txn)
              ) {
                mutable.add(txn);
              }
            });
          }
        });
      });
      setUIState({ allSelected: true, selectedTxns });
    }
    resetFocus();
  };

  const detailsRefSaveFn = (reff: () => void) => {
    setUIState({ detailsRefSaveFn: reff });
  };

  const openCloseSection = (sectionKey: string) => {
    let newSections;
    if (uiState.openSections?.has(sectionKey)) {
      newSections = uiState.openSections.delete(sectionKey);
    } else {
      newSections = uiState.openSections?.add(sectionKey);
    }
    setUIState({ openSections: newSections });
    resetFocus();
  };

  const markTxnDirty = () => {
    setUIState({ txnDirty: true });
  };

  const makeQDialogCallback = (cb) => ((passedValue: string) => {
    forceUpdate();
    cb(passedValue);
  });

  const makeNewTxn = (acct) => {
    const accountRec = props.accountsById.get(acct);
    const isConnected = accountsUtils.isActiveConnectedAccount(accountRec);

    return new transactionsTypes.CashFlowTransaction({
      accountId: acct,
      isReviewed: true,
      clientId: uuidv4().toUpperCase(),
      state: isConnected ? 'PENDING' : 'CLEARED',
    });
  };

  const showNewTxn = (
    newTransaction: undefined | CashFlowTransaction = undefined,
    passive: boolean = false,
  ) => {
    let acct = (newTransaction && newTransaction.accountId)
      || uiState.lastAccountEntered
      || firstBankAccount(accountIds);
    if (accountIds.size > 0 && !accountIds.includes(acct)) {
      acct = accountIds.first();
    }
    let newTxn = makeNewTxn(acct);
    if (newTransaction) {
      newTxn = newTxn.merge(newTransaction);
    }

    const uiStateObj = setTxnBeingEdited(
      newTxn,
      {
        lastAccountEntered: acct,
        scrollToId: null, // todo: make sure this scrolls up on inline create
        showNewTxn: true,
        simpleEdit: true,
        showSplitsAndDetails: false,
        showSplitWindow: false,
      },
    );

    return passive ? uiStateObj : setUIState(uiStateObj);
  };

  // options
  // flashTx = show animation if dirty, and saved (default true)
  // txDirty = transaction has been changed (default true)
  // showNew = after save, open the new transaction form (default false)
  // cancelEdit = cancel current edit after save
  // cb - callback, will send true if saved perform, otherwise false
  const saveTransactionWithActions = (
    txn: transactionsTypes.CashFlowTransaction,
    { flashTx = true, txnDirty = true, showNew = false, doCancelEdit = true, lastAccountEntered = null }: Record<string, any>,
    cb: (val: any) => void,
  ) => {
    saveTransaction(txn, (saved) => {
      if (saved) {
        const flashId = isExistingTxn(txn) ? txn.id : txn.clientId;
        let stateObj: RegisterUIStateType = { lastAccountEntered };
        if (doCancelEdit) {
          stateObj = cancelEdit({ ...stateObj, txnDirty, flashTx: (flashTx && !txnDirty ? flashId : null) }, true);
        }
        if (showNew) {
          stateObj = showNewTxn(new transactionsTypes.CashFlowTransaction({ accountId: txn.accountId }), true);
          stateObj.currEditFieldName = 'payee';
        }
        setUIState(stateObj);
      }
      if (cb) {
        cb(saved);
      }
    });
  };

  const saveAndCancel = (
    txn: CashFlowTransaction,
    cb: any = null,
  ) => {
    const { txnBeingEdited } = uiState;
    const savingTxnBeingEdited = txnBeingEdited && (txn.id === txnBeingEdited.id);
    const wasNew = uiState.showNewTxn && savingTxnBeingEdited;

    const options = {
      txnDirty: uiState.txnDirty && !savingTxnBeingEdited,
      flashTx: savingTxnBeingEdited,
      showNew: wasNew && props.continuousCreate,
      cancelEdit: true,
      lastAccountEntered: txn.accountId,
    };

    saveTransactionWithActions(txn, options, (saved) => {
      if (cb) {
        cb(saved);
      }
    });
  };

  //------------------------------------------------------
  // txFormUpdate
  // replace the txForm state (current row being edited) with the
  // new txForm provided
  //
  // Use this when you have a JS structure (not immutable)
  const txFormUpdate = (txForm, isAutoSave = false, txnDirty = true, splitToggle = undefined) => {
    const newTxn = transactionsTransformers.transformApiDataToCashFlowTxn(txForm).set('balance', txForm.balance);
    // if this txn was selected, add the new one to the selection
    setTxnBeingEdited(newTxn);
    // If this transaction is not valid (this happens during Splits creation) do not try to autosave
    const doAutoSave = false;
    // console.log("TX FORM UPDATE", newTxn, isAutoSave, txnDirty, doAutoSave);
    if (txnDirty && isAutoSave) {
      saveAndCancel(setTxnReviewed(newTxn));
    } else if (!doAutoSave) {
      setUIState({
        txnDirty,
        txnBeingEdited: newTxn,
        showSplitWindow: splitToggle === undefined ? uiState.showSplitWindow : splitToggle,
      });
    }
  };

  const saveUnbalancedSplitTxn = (txn, cb) => {
    saveAndCancel(txn, cb);
  };

  // Save IF the transaction is dirty or is the new txn
  const saveTxnBeingEdited = (cb?: any) => {
    const txn = uiState.txnBeingEdited;
    if (txn && uiState.txnDirty) {
      if (isUnbalancedSplitTxn(txn)) {
        return saveUnbalancedSplitTxn(txn, cb);
      }
      saveAndCancel(txn, cb);
    } else {
      return cancelEdit();
    }

    return true;
  };

  const saveTxn = (
    txn: CashFlowTransaction,
    updatedUiState: RegisterUIStateType,
    cb: any = null,
  ) => {
    const { txnBeingEdited } = updatedUiState;
    const savingTxnBeingEdited = txnBeingEdited && (txn.id === txnBeingEdited.id);
    const wasNew = updatedUiState.showNewTxn && savingTxnBeingEdited;

    const options = {
      txnDirty: updatedUiState.txnDirty && !savingTxnBeingEdited,
      flashTx: savingTxnBeingEdited,
      showNew: wasNew && props.continuousCreate,
      cancelEdit: true,
      lastAccountEntered: txn.accountId,
    };

    saveTransactionWithActions(txn, options, (saved) => {
      if (cb) {
        cb(saved);
      }
    });
  };

  const saveUpdatedTxnBeingEdited = (updatedUiState: RegisterUIStateType, cb?: any) => {    
    const txn = updatedUiState.txnBeingEdited;
    if (txn && updatedUiState.txnDirty) {
      if (isUnbalancedSplitTxn(txn)) {
        return saveUnbalancedSplitTxn(txn, cb);
      }
      saveTxn(txn, updatedUiState, cb);
    } else {
      return cancelEdit();
    }

    return true;
  };

  const maybeSaveTxnBeingEdited = (cb?: any) => {
    if (!uiState.txnBeingEdited || !uiState.txnDirty) {
      if (cb) cb(true);
    } else if (!props.autoSave) { // } || confirm('Do you want to save the changed transaction?')) {
      dialogAlert(
        'Save Transaction',
        'Do you want to save the changes to the transaction being edited?',
        makeQDialogCallback((retObj) => {
          if (retObj.btnPressed === 'Save Transaction') {
            return saveTxnBeingEdited(cb);
          }
          if (retObj.btnPressed === "Don't Save") {
            if (cb) {
              cb(true);
            }
          }
          if (cb) {
            cb(false);
          }
          return undefined;
        }),
        ['Save Transaction', "Don't Save", 'Cancel'],
        'help',
      );
    } else {
      saveTxnBeingEdited(cb);
    }
  };

  // This is where we update state of current edit field based on changes
  // global now with specific checks, TODO: make this more configuration driven, not if statements
  const handleChange = (event, field, rawValue) => {
    // don't deal with null events
    if (!event) {
      return;
    }

    let txn = uiState.txnBeingEdited;
    const value = event.target?.value || rawValue || null;
    const isCancelEdit = false;
    const doSimplifiRules = Boolean(datasetPreferences?.defaultCreateRuleToChecked);
    if (txn) {
      switch (field) {
        case 'category':
          txn = txn.set('coa', value);
          if ((event.target.doPropogate || doSimplifiRules) && (Number(txn.id) !== 0) && !transactionsUtils.isSplitTxn(txn)) {
            // should apply rules
            const priorTxn = txn.id ? txnsById.get(txn.id) : undefined;
            const priorCoa = priorTxn?.coa;
            if (priorCoa && priorCoa?.id !== txn?.coa?.id) {
              // has valid COA for rules
              if (doSimplifiRules) {
                // show Simplifi rule dialog
                showRootDialog({
                  id: 'add-memorized-rule',
                  type: DIALOG_TYPE_MEMORIZED_RULE_EDIT,
                  allowNesting: true,
                  props: ImmutableMap({
                    memorizedRule: {
                      payee: priorTxn.payee,
                      coa: value,
                    },
                    fromTxn: true,
                  }),
                });
                quickSaveTransaction(txn, true, isCancelEdit); // then save current txn

              } else if (!transactionsUtils.isTransferTxn(txn)) {
                // auto-apply for Quicken
                updateTransactionCategoriesForPayeeAction({
                  priorCoa,
                  payee: txn.payee,
                  coa: txn.coa,
                });
                makeMemorizedRuleFromTransaction(txn);
              }
              return;
            }
          }
          quickSaveTransaction(txn, true, isCancelEdit);
          return;

        case 'postedOn':
          txn = txn.set(field, event.target.value);
          quickSaveTransaction(txn, true);
          return;

        case 'amount':
          txn = txn.set(field, value);
          break;
        case 'income':
          txn = txn.set('amount', value);
          break;

        case 'expense':
          txn = txn.set('amount', value);
          break;

        case 'check':
          if (!txn.check) {
            txn = txn.set('check', {});
          }
          txn = txn.setIn(['check', 'number'], value || '');
          break;
        case 'tags':
          txn = txn.set('tags', event);
          // if any of the tags are not yet created, then delay the auto-save to minimize errors (no guarantee tho)
          if (event.find((x) => !x.id)) {
            setUIState(setTxnBeingEdited(txn, { txnDirty: true }));
            setTimeout(() => quickSaveTransaction(txn as CashFlowTransaction, true, isCancelEdit), 200);
          } else {
            quickSaveTransaction(txn, true, isCancelEdit);
          }
          return;

        case 'notes':
          txn = txn.set('memo', value);
          break;

        case 'account':
          if (event && event !== txn.accountId) {
            // make sure this is an ok account to use
            // @ts-expect-error - FIXME: Expected 4 arguments, but got 3.ts(2554)
            if (accountsUtils.transactionsNotSupportedForAccountId(event, hideLoanTransactions, hideConnectedLoanTransactions)) {
              dialogAlert(
                'Cannot Change Account',
                'Sorry, but the account you selected does not support transactions.',
                makeQDialogCallback(noop),
                ['Close'],
                'warning',
              );
            } else {
              txn = txn.set('accountId', event);
              quickSaveTransaction(txn, true, isCancelEdit);
              // this.setUIState(this.setTxnBeingEdited(txn, { txnDirty: false }));
              break;
            }
          } else {
            return;
          }
          break;

        case 'payee': {
          // Payee item was chosen from the payee drop down, which includes the original transaction information
          // We "Quickfill" that information into the
          // log.debug('ON CHANGE PAYEE ', value, this.uiState.txnBeingEdited.payee);

          // IF the payee is returned as a structure, that means it was selected out of the payee list, or
          // it means that it was a tab out of the field, and the structure is returned to represent "SELECT"
          // different from on change
          txn = txn.set(field, value?.name ?? value);
          if (value && value.txn) {
            txn = updateMemorizePayeeValues(txn, value);
            quickSaveTransaction(txn as CashFlowTransaction, true);
            return;
          }
          if (value && value.name && value.action === 'Tab') {
            quickSaveTransaction(txn, true);
          }
          break;
        }
        case 'state':
          txn = txn.set(field, event);
          quickSaveTransaction(txn);
          return;

        default:
          txn = txn.set(field, event.target.value);
          break;
      }
      txn = setTxnReviewed(txn);
      setUIState(setTxnBeingEdited(txn as CashFlowTransaction, { txnDirty: true }));
    }
    // log.log('Changing txn', txn, `>${field}<`);
  };

  const fastCategorize = () => {
    // make a list of all uncategorized transactions with a suggestion
    const num = transactionsWithCategorySuggestions.size;
    if (num) {
      dialogAlert(
        `We found ${num} uncategorized transaction${num > 1 ? 's' : ''} we can categorize for you`,
        'This action will categorize all of them based on prior history.',
        makeQDialogCallback((retObj) => {
          if (retObj.btnPressed !== 'Cancel') {
            const newTransactions = transactionsWithCategorySuggestions.map((txn) => {
              const newTxn = setTxnReviewed(txn);
              return newTxn.set('coa', payeesForAccounts.get(txn.payee?.toLowerCase()).txn.coa);
            });

            // if (retObj.btnPressed.indexOf('memo') > 0) {
            // newTransactions = newTransactions.map((txn) =>
            //   txn.set('memo', `${txn.memo ? `${txn.memo} - ` : ''}Auto Categorized`));
            // }
            qDataSaveTransactions(newTransactions);
          }
        }),
        ['Cancel', 'Auto Categorize'],
        'info',
        false,
        true,
      );
    } else {
      dialogAlert(
        'We did not find any uncategorized transactions we can categorize for you',
        'We rely on prior history to determine a category suggestion',
        makeQDialogCallback(() => null),
        ['Ok'],
        'info',
        false,
        true,
      );
    }
  };

  const checkForC2REnabledAccountInBulkReview = (txns) => {
    const hasTrue = txns.some((txn: CashFlowTransaction) => isC2REnabledAccount(txn.accountId));
    const hasFalse = txns.some((txn: CashFlowTransaction) => !isC2REnabledAccount(txn.accountId));

    if (hasTrue && hasFalse) {
      return 'N/A';       // mixed
    }
    if (hasTrue) {
      return true;        // All C2R enabled
    } 
    return false;         // NON c2r enabled
  };

  const reviewTransactions = (txns, value) => {
    if (txns) {
      const newTxns = txns.map((txn) => {
        let updatedTxn = txn.set('isReviewed', value);
        if (txn.source === TxnPendingStates.QCS_REFRESH_BANK_PENDING) {
          updatedTxn = setTxnUserOwnedBankPending(updatedTxn);
        }
        return updatedTxn;
      });

      qDataSaveTransactions(newTxns, (success) => {
        if (success) {
          tracker.track(tracker.events.txnReviewMany, {
            state: 'complete',
            setReviewed: value,
            count: txns.size,
          });

          // send event only when it is reviewed
          if (value) {
            tracker.track(tracker.events.txnReview, {
              isC2REnabled: isMacDataset ? false : checkForC2REnabledAccountInBulkReview(txns),
              action: 'bulkReviewed',
              count: txns.size,
            });
          }
        }
      });
    }
  };

  const reviewSelectedTxns = (setReviewed) => {
    const num = uiState.selectedTxns?.size ?? 0;
    tracker.track(tracker.events.txnReviewMany, {
      state: 'attempt',
      count: num,
      setReviewed,
    });

    const doReview = () => reviewTransactions(uiState.selectedTxns, setReviewed);
    dialogAlert(
      `Mark ${num} Selected Transaction${num > 1 ? 's' : ''} as ${setReviewed ? 'Reviewed' : 'Not Reviewed'}`,
      'This action will mark all the selected transactions',
      makeQDialogCallback((retObj) => {
        if (retObj.btnPressed === 'Continue') {
          doReview();
        }
      }),
      ['Cancel', 'Continue'],
      'help',
      false,
    );
  };

  const deleteTransactions = (txns, isBulkDelete = false) => {
    qDataDeleteTransactions(txns, { isBulkDelete });
    if (typeof props.onTxnUpdate === 'function') {
      setTimeout(() => props.onTxnUpdate(), 1000);
    }
  };

  const deleteSelectedTxns = () => {
    const num = uiState.selectedTxns?.size;
    uiState.selectedTxns?.forEach((x) => {
      tracker.track(tracker.events.txnDelete, {
        state: 'attempt',
        type: x.cpData ? 'downloaded' : 'manual',
      });
    });

    if (!props.deleteDownloaded) {
      if (uiState.selectedTxns?.find((x) => Boolean(x.cpData))) {
        dialogAlert(
          'You cannot delete downloaded transactions',
          'You selected 1 or more transactions to delete which were downloaded' +
          ' by your financial institution.  It is not recommended that you delete data from your ' +
          ' bank as it will not be downloaded again. ' +
          ` ${props.openPrefs ? ' However, if you really want to remove it you can ' +
            ' alter the preference in your transaction settings ' : ''}`,
          makeQDialogCallback((retObj) => {
            if (retObj.btnPressed === 'Open Preferences' && props.openPrefs) {
              props.openPrefs();
            }
          }),
          props.openPrefs ? ['Open Preferences', 'Cancel'] : ['Cancel'],
          'warning',
        );
        return;
      }
    }

    dialogAlert(
      `Delete ${num} Selected Transaction${num! > 1 ? 's' : ''}`,
      'This action will delete all the selected transactions and cannot be undone.',
      makeQDialogCallback((retObj) => {
        if (retObj.btnPressed === 'Delete') {
          deleteTransactions(uiState.selectedTxns, true);
        }
      }),
      ['Cancel', 'Delete'],
      'delete',
      false,
    );
  };

  const deleteTx = (txn) => {
    if (txn !== uiState.txnBeingEdited) {
      maybeSaveTxnBeingEdited((proceed) => {
        if (proceed) {
          deleteTransactions([txn]);
        }
      });
    } else {
      cancelEdit();
      deleteTransactions([txn]);
    }
  };

  const actOnScheduledTxn = (txn, action) => {
    const actionObj = {
      id: txn.id,
      action,
      overdueAction: isMacDataset ? 'SKIP' : action,
      instanceDate: txn.stDueOn,
    };
    qDataPerformTransactionAction(actionObj);
  };

  const matchSelectedTxns = () => {
    tracker.track(tracker.events.txnManualMatch, {
      state: 'attempt',
    });
    const buttonText = 'Proceed with Match';
    const companionBodyText = ` This action will merge the two selected transactions into one and automatically mark it as "${isMacDataset ? 'unreviewed' : 'reviewed'}". The payee category, notes, and tags from the manual transaction will be preserved. 
      ${isMacDataset ? 'You can "Unmatch" this transaction at any time.' : ''}`;
    dialogAlert(
      'Match Selected Transactions',
      companionBodyText,
      makeQDialogCallback((retObj) => {
        if (retObj.btnPressed === buttonText) {
          qDataMatchTransactions(uiState.selectedTxns?.toArray());
        }
      }),
      ['Cancel', buttonText],
      'match',
      false,
    );
  };

  const saveDoc = (passedProps) => {
    const { txn, docId } = passedProps;
    log.log('SAVING ATTACHMENTS', txn, docId);

    let newTxn: null | CashFlowTransaction = null;

    if (txn && docId) {
      if (txn.attachments) {
        const newArray: Attachment[] = txn.attachments;
        newArray.push({ id: docId });
        newTxn = txn.set('attachments', newArray);
      } else {
        const newArray: Attachment[] = [];
        newArray.push({ id: docId });
        newTxn = txn.set('attachments', newArray);
      }
      // TODO I could only get an update to happen by changing a field other than attachments
      // even though attachments is a different value, no update
      // but if I change the memo - it updates.  Bizarre.
      if (newTxn) {
        const { memo } = newTxn;
        newTxn = newTxn.set('memo', '2');
        newTxn = newTxn.set('memo', memo);
        saveTransaction(newTxn);
      }
      setUIState(clearTxnBeingEdited({
        showDocumentAdd: false,
        txnDirty: false,
      }));
    }
  };

  const delDocIds = (idArray) => {
    let txn = uiState.txnBeingEdited;
    if (txn?.attachments) {
      const newArray = txn.toJS().attachments ?? [];
      const pluralChar = idArray.length > 1 ? 's' : '';
      dialogAlert(
        `Delete Attachment${pluralChar}`,
        `This will delete the selected attachment${pluralChar}`,
        (retObj) => {

          if (retObj.btnPressed === 'Delete') {
            idArray.forEach((id) => {
              const idx = newArray.findIndex((obj) => obj.id === id);
              if (idx >= 0) {
                newArray.splice(idx, 1);
              } else {
                log.error('ERROR: Bad ID in list to delete', 'error');
              }
              // commenting this out orphans the id, but preserves if user does not save
              deleteDocument({ id });
              if (txn?.attachments?.length === 0) {
                txn = txn.set('attachments', null);
              }
            });

            const { memo } = txn!;
            txn = txn?.set('memo', '2');
            txn = txn?.set('attachments', newArray);
            txn = txn?.set('memo', memo);

            clearTxnBeingEdited();
            setUIState(setTxnBeingEdited(txn!, {
              txnDirty: false,
              txnBeingEdited: txn,
            }));

            saveTransaction(txn!);
          }
        },
        ['Cancel', 'Delete'],
        'delete',
      );
    }
  };

  const blurCurrentEditField = () => {
    if (currentFocusField.current) {
      currentFocusField.current.blur();
    }
  };

  //------------------------------------------------------
  // bulkEditSave
  //
  // returns an object with new values to be applied to all
  // non-split transactions
  const bulkEditSave = (fields) => {
    if (Object.keys(fields).length > 0) {
      const { coa, tags: fieldTags, ...otherFields } = fields;
      const newTxns = uiState.selectedTxns?.map((item) => {
        let newTxn = setTxnReviewed(item);
        if (!transactionsUtils.isSplitTxn(item)) {
          if (coa) {
            newTxn = newTxn.set('coa', coa);
          }
          if (fieldTags) {
            newTxn = newTxn.set('tags', newTxn.tags ? newTxn.tags.merge(fieldTags) : fieldTags);
          }
          if (item.source === TxnPendingStates.QCS_REFRESH_BANK_PENDING) {
            newTxn = newTxn.set('source', TxnPendingStates.USER_OWNED_BANK_PENDING);
          }
        }
        return newTxn.merge(otherFields);
      }) ?? ImSet();
      const saveSelectedTxns = () => {
        log.log('SAVING TRANSACTIONS', newTxns.toJS());
        qDataSaveTransactions(newTxns);
        setUIState({ selectedTxns: ImSet() });
      };

      // only suppressed when the user has explicitly opted out via the dialog or settings
      if (datasetPreferences?.transactionRegister?.showMultiEditAlert === false) {
        saveSelectedTxns();
        return;
      }

      dispatch(showBulkEditConfirmDialog({
        count: newTxns.size,
        hasSplitTxns: Boolean(uiState.selectedTxns?.some((item) => transactionsUtils.isSplitTxn(item))),
        onConfirm: saveSelectedTxns,
        onCancel: forceUpdate,
        onDontShowAgain: () => setDatasetPreference({ transactionRegister: { showMultiEditAlert: false } }),
      }));
    }
  };

  const editSelectedTxns = () => {
    const { selectedTxns } = uiState;
    if (selectedTxns && selectedTxns?.size > 0) {
      // warn and fail if the bulk edit contains both sides of a transfer
      setUIState({ showBulkEdit: true });
    }
  };

  const showNewTxnMaybePrompt = (newTransaction = undefined) => {
    maybeSaveTxnBeingEdited((proceed) => {
      if (proceed) {
        showNewTxn(newTransaction, false);
      }
    });
  };

  // When a field gets focus, select all it's contents
  const handleFocus = (event) => {
    event.target.select();
  };

  const handleMenu = (action: string, txn: CashFlowTransaction, value?: any) => {
    log.log('HANDLE MENU ==> ', action, txn);
    setUIState({ showEditMenu: null });
    const payeeField = 'payee'; // uiState.regEditableFields.indexOf('payee');
    let newTxn;
    /* eslint-disable no-case-declarations */
    switch (action) {
      case 'state':
        switch (value) {
          case 'delete':
            dispatchDeleteReminderDialog(txn);
            break;

          case 'match':
            dispatchShowMatchTxnDialog(txn);
            break;

          case 'deleteModel':
            dialogAlert(
              'Delete Recurring Bill/Income?',
              'This will remove this transaction from the recurring list, and will not create future instances any longer.',
              makeQDialogCallback((retObj) => {
                if (retObj.btnPressed === 'Delete') {
                  const resource = txn?.stModelId ? scheduledTransactionsById.get(txn.stModelId) : null;
                  if (resource) {
                    deleteScheduledTransaction(resource);
                  } else {
                    dialogAlert(
                      'Recurring Model Not Found',
                      'Could not find the recurring model for this transaction.  Do you want to delete this reminder?',
                      makeQDialogCallback((ret) => {
                        if (ret.btnPressed === 'Delete Instance') {
                          deleteTransactions([txn]);
                        }
                      }),
                      ['Delete Instance', 'Close'],
                      'error',
                    );
                  }

                }
              }),
              ['Delete', 'Cancel'],
              'delete',
            );
            break;

          default:
            newTxn = txn.set('state', value);
            newTxn = setTxnReviewed(newTxn);
            if (isExistingTxn(txn) && txn !== uiState.txnBeingEdited) {
              saveTransaction(newTxn);
            } else {
              setUIState(setTxnBeingEdited(newTxn, { txnDirty: true }));
            }
        }
        break;

      case 'reviewed':
        newTxn = txn.set('isReviewed', !txn.isReviewed);
        if (isExistingTxn(txn) && (txn !== uiState.txnBeingEdited)) {
          saveAndCancel(newTxn, undefined);
        } else {
          setUIState(setTxnBeingEdited(newTxn, { txnDirty: true }));
        }
        break;


      case 'userFlag':
        newTxn = txn.set('userFlag', value);
        newTxn = setTxnReviewed(newTxn);
        if (isExistingTxn(txn) && (txn !== uiState.txnBeingEdited)) {
          saveAndCancel(newTxn);
        } else {
          setUIState(setTxnBeingEdited(newTxn, { txnDirty: true }));
        }
        break;

      case 'ignored':
        newTxn = txn.set('isExcludedFromReports', value?.isExcludedFromReports);
        newTxn = newTxn.set('isExcludedFromF2S', value?.isExcludedFromF2S);
        newTxn = setTxnReviewed(newTxn);
        if (isExistingTxn(txn)) {
          saveAndCancel(newTxn);
        } else {
          setUIState(setTxnBeingEdited(newTxn, { txnDirty: true }));
        }
        break;

      case 'save':
        // if details/splits are open, then we need to save through them
        if (!uiState.simpleEdit || uiState.showSplitWindow) {
          if (uiState.detailsRefSaveFn) {
            // calls the save function of the SplitsPanel component, which houses both splits and edit details
            uiState.detailsRefSaveFn();
          }
        } else {
          saveTxnBeingEdited();
        }        
        if (typeof props.onTxnUpdate === 'function') {
          setTimeout(() => props.onTxnUpdate(), 1000);
        }
        break;
      case 'transfer':
        if (transactionsUtils.isTransferTxn(txn) && isSupportedTransferTxn(txn)) {

          let destAcct: null | string = null;
          let altAmount = null;
          if (transactionsUtils.isSplitTxn(txn) && txn && txn.split?.items) {
            txn.split.items.forEach((item) => {
              if (item.coa.type === 'ACCOUNT') {
                destAcct = item.coa.id;
                altAmount = item.amount;
              }
            });
          } else {
            destAcct = txn?.coa ? txn.coa.id : null;
          }

          if (destAcct) {
            const matchObj = transactionsUtils.findMatchingTransferTx(txn, txnsByAccountId.get(destAcct), altAmount);

            if (matchObj && props.navFn) {
              props.navFn(matchObj.txn.accountId, matchObj.txn.id);
            }
          }
        }
        break;
      case 'showDetails':
        let isTxnDirty = uiState.txnDirty;
        if (uiState.txnBeingEdited?.id !== txn.id) {
          isTxnDirty = false;
        }
        setUIState(setTxnBeingEdited(txn, {
          showSplitWindow: transactionsUtils.isSplitTxn(txn),
          simpleEdit: false,
          currEditFieldName: payeeField,
          txnDirty: isTxnDirty,
        }));
        break;
      case 'hideDetails':
        setUIState(setTxnBeingEdited(setTxnReviewed(txn), {
          showSplitWindow: false,
          simpleEdit: true,
          currEditFieldName: payeeField,
        }));
        break;
      case 'addAttachments':
        setUIState(setTxnBeingEdited(setTxnReviewed(txn), { showDocumentAdd: true, currEditFieldName: payeeField }));
        break;
      case 'viewAttachments':
        setUIState({ showDocumentBrowserTxn: txn });
        break;
      case 'split':
        setUIState(setTxnBeingEdited(setTxnReviewed(txn), { showSplitWindow: true, currEditFieldName: payeeField }));
        break;
      case 'hideSplit':
        setUIState(setTxnBeingEdited(setTxnReviewed(txn), { showSplitWindow: false, currEditFieldName: payeeField }));
        break;
      case 'unMatch': {
        const fromReminder = (txn && txn.source === 'SCHEDULED_TRANSACTION');
        const verb = 'match';
        const mainCopy = fromReminder ? 'This will unlink your reminder from this downloaded transaction.' :
          'This will unlink your manual transaction from this downloaded transaction';
        dialogAlert(
          `Un${verb} Transaction`,
          mainCopy,
          makeQDialogCallback((retObj) => {
            if (retObj.btnPressed === 'Continue') {
              qDataUnMatchTransaction(txn);
              cancelEdit();
            }
          }),
          ['Continue', 'Cancel'],
          'match',
        );
        break;
      }
      case 'delete':
        tracker.track(tracker.events.txnDelete, {
          type: txn.cpData ? 'downloaded' : 'manual',
          state: 'attempt',
        });
        if (txn.cpData && !props.deleteDownloaded) {
          dialogAlert(
            "Can't Delete Transaction",
            'This is a downloaded transaction and cannot be deleted.',
            makeQDialogCallback(noop),
            ['Close'],
            'delete',
            false,
            true,
          );
        } else {
          dialogAlert(
            'Delete Transaction',
            'Are you sure you want to delete this transaction?',
            makeQDialogCallback((retObj) => {
              if (retObj.btnPressed === 'Yes') {
                deleteTx(txn);
              }
            }),
            undefined,
            'delete',
            false,
            true,
          );
        }
        break;
      case 'xfer':
        // TODO: Keeping this commented after migration to FC & TS
        // if (props.navFn) {
        //  props.navFn(uiState.txForm.coa.id, props.account.id, obj.id);
        // }
        break;
      default:
        break;
    }
  };

  const onAmountBlur = (event, field, wasEnter) => {    
    let txn = uiState.txnBeingEdited!;
    const newVal = normalizeAmt(Number(event.target.value), field === 'expense' ? -1 : 1);

    if (Number(newVal) !== Number(txn.amount)) {
      if (field === 'amount' || Number(newVal)) { // if income/expense field, must have a value
        txn = txn.set('amount', newVal);
        txn = balanceSplitTxn(txn);
        const updatedUiState = setUIState(setTxnBeingEdited(txn, {
          txnDirty: true,
        }));
        if (wasEnter) {          
          if ((!uiState.simpleEdit || uiState.showSplitWindow)) {
            // @ts-expect-error - FIXME: An argument for 'txn' was not provided.
            setTimeout(() => handleMenu('save'), 5);
          } else {
            saveUpdatedTxnBeingEdited(updatedUiState);
            if (typeof props.onTxnUpdate === 'function') {
              setTimeout(() => props.onTxnUpdate(), 1000);
            }
          }          
        }        
      }
    } else if (wasEnter) {
      // @ts-expect-error - FIXME: An argument for 'txn' was not provided.
      setTimeout(() => handleMenu('save'), 5);
    }
  };

  const reminderChange = (e) => {
    if (e !== props.reminderSetting) {
      // if this is a change, write to preferences
      if (accountIds && accountIds.size === 1) {
        const acctRec = props.accountsById.get(accountIds.first());
        // update the account record, but only update the critical fields
        if (acctRec) {
          const recurringTxn = accountsUtils.getReminderObjectFromDays(e);
          const newAcct = accountsTypes.mkUpdateAccount(acctRec.type, { id: acctRec.id, recurringTxn });
          updateAccount(newAcct);
          tracker.track(tracker.events.prefScheduleTransactionVisibility, recurringTxn);
        }
      }
    }
  };

  const headerClick = (_: MouseEvent<HTMLElement>, field: string) => {
    let sortField: string;
    let sortAnimation: boolean = false;
    let newSortOrder;
    switch (field) {
      case 'postedOn':
        sortField = 'date';
        break;
      case 'state':
        sortField = 'status';
        break;
      case 'reviewed':
        return;
      case 'expense':
      case 'income':
        sortField = 'amount';
        break;

      default:
        sortField = field;
    }

    if (sortField === sortBy) {
      newSortOrder = sortOrder === 'ascending' ? 'descending' : 'ascending';
      sortAnimation = true;
    }

    if ((sortField !== sortBy || newSortOrder !== sortOrder) && props.onSortChange) {
      props.onSortChange(sortField, newSortOrder);
    }
    setUIState({ txnsFirstLoaded: acctTxns, sortAnimation });
  };

  const hideModal = (modalStr: string) => {
    const payeeField = 'payee'; // this.uiState.regEditableFields.indexOf('payee');
    switch (modalStr) {
      case 'bulkEdit':
        setUIState({ currEditFieldName: payeeField, showBulkEdit: false });
        resetFocus();
        break;
      case 'splits':
      case 'details':
        setUIState({
          currEditFieldName: payeeField,
          simpleEdit: true,
          showSplitWindow: false,
        });
        // TODO: Check the resetFocus as callback in this.forceUpdate later.
        forceUpdate();
        resetFocus();
        break;
      case 'documentBrowser':
        setUIState({ currEditFieldName: payeeField, showDocumentBrowserTxn: null });
        break;
      case 'addDocument':
        setUIState({ currEditFieldName: payeeField, showDocumentAdd: false });
        break;
      default:
        break;
    }
  };

  // Routines to control the tabbing order (forward and back) taking into account fields that
  // should be skipped for this transaction
  // Props must have:
  // accountIds, editRunningBalance, regEditableFields, headerFields, editDownloaded
  // showAccountColors, showSplitWindow, scheduledTransactionsById, txnExtraColumn
  const isSkipField = (passedProps, fld, txn) => {
    const { editDownloaded, showAccountColors } = passedProps;
    const options = {
      accountIds,
      allAccountIds,
      editRunningBalance: false,
      scheduledTransactionsById,
      editDownloaded,
      txnCancelExtraColumn,
      showAccountColors,
      headerFields: props.regFieldsOrder,
      regEditableFields: uiState.regEditableFields,
      showSplitWindow: uiState.showSplitWindow,
    };

    return !helpers.fieldIsEditable(txn, fld, options);
  };

  const getNextField = (passedProps) => {
    const { headerFields } = passedProps.uiState;
    const txn = passedProps.uiState.txnBeingEdited;
    const currIndex = headerFields.indexOf(passedProps.uiState.currEditFieldName);
    let nextFieldIndex = (currIndex + 1) >= headerFields.size ? 0 : currIndex + 1;
    // Skip the inactive fields
    while (isSkipField(passedProps, headerFields.get(nextFieldIndex), txn)) {
      nextFieldIndex = nextFieldIndex + 1 >= headerFields.size ? 0 : nextFieldIndex + 1;
    }

    return headerFields.get(nextFieldIndex);
  };

  const getPrevField = (passedProps) => {
    const { headerFields } = passedProps.uiState;
    const txn = passedProps.uiState.txnBeingEdited;
    const currIndex = headerFields.indexOf(passedProps.uiState.currEditFieldName);
    let nextFieldIndex = (currIndex - 1) < 0 ? headerFields.size - 1 : currIndex - 1;
    // Skip the inactive fields
    while (isSkipField(passedProps, headerFields.get(nextFieldIndex), txn)) {
      nextFieldIndex = nextFieldIndex - 1 < 0 ? headerFields.size - 1 : nextFieldIndex - 1;
    }

    return headerFields.get(nextFieldIndex);
  };

  // changeEditableField
  //
  // either will update an object you are preparing for a setUIState,
  // or else it will call setUIState directly
  //
  const changeEditableField = (field: string, updateObject?: Record<string, any>) => {
    if (updateObject) {
      const newObj = copyObject(updateObject);
      newObj.currEditFieldName = field;
      return newObj;
    }

    return setUIState({ currEditFieldName: field });
  };

  //-------------------------------------------------------
  // regFieldKey
  // called when current edit field gets a keypress, used to
  // process special keys like tab, enter, and ctrl
  //
  const regFieldKey = (event) => {
    const shifted = event.shiftKey;
    const field = uiState.currEditFieldName;
    // CTRL KEYS
    if (event.ctrlKey) {
      switch (event.key) {
        case 's':
          if (uiState.txnBeingEdited) {
            handleMenu('split', uiState.txnBeingEdited);
          }
          break;
        case 'l':
          if (uiState.txnBeingEdited) {
            handleMenu('showDetails', uiState.txnBeingEdited);
          }
          break;
        /*
        case 'd':
          if (uiState.txnBeingEdited) {
            handleMenu('delete', uiState.txnBeingEdited);
          }
          break;
        */
        case 'x':
          if (uiState.txnBeingEdited) {
            handleMenu('transfer', uiState.txnBeingEdited);
          }
          break;
        default:
          break;
      }
    }

    switch (event.key) {
      case 'Escape':
        cancelEdit();
        event.stopPropagation();
        break;
      case 'Tab':
      case 'Enter':
        if (uiState.txnBeingEdited) {
          event.preventDefault();
          event.stopPropagation();
          if (event.key === 'Tab') {
            // if (field !== 'payee') blurCurrentEditField();
            if (field !== 'payee') blurCurrentEditField();
            let target: string = field as string;
            if (!shifted) {
              // advance
              target = getNextField({ ...props, uiState });
            } else {
              target = getPrevField({ ...props, uiState });
            }
            changeEditableField(target);
          }
        }
        break;
      default:
    }

    if (event.key === 'Enter') {
      // should always be true for Quicken (not autoSave)
      // this is a 'nextTick', to allow the blur field above to execute before the save
      if (field !== 'amount') {        
        // @ts-expect-error - FIXME: An argument for 'txn' was not provided.
        handleMenu('save');
      }
    }
  };

  const removeSplitsFromTxnsAndUpdateUiState = (txn) => {
    const newTxn = new transactionsTypes.CashFlowTransaction({
      ...txn,
      coa: txn.split?.items[0]?.coa || null,
      tags: txn.split?.items[0]?.tags || null,
      split: null,
    });
    setUIState({ txnBeingEdited: newTxn, showSplitWindow: false, txnDirty: true });
  };

  //-------------------------------------------------------
  // regFieldClick
  // when a click happens on a field, move to it if it is editable
  //
  const regFieldClick = (vfield, txn, suggestTxn) => {
    log.debug('=================== Clicking on register field', vfield, txn.payee);

    let catSuggest = false;
    let openSplit = false;
    let field = vfield;
    if (vfield === 'categorySuggest') {
      field = 'category';
      catSuggest = true;
    } else if (vfield === 'split') {
      openSplit = true;
      field = 'category';
    }
    const fieldBeingClicked = isSkipField(props, field, txn) ? null : field;
    // CLICK IS WITHIN THE CURRENTLY BEING EDITED TRANSACTION
    if (txn === uiState.txnBeingEdited) {
      if (vfield === 'clearSplits') {
        if (txn.id) {
          dialogAlert(
            'Clear All Splits',
            'This action will delete all the split categories within the transaction?',
            ({ btnPressed }) => btnPressed === 'CLEAR SPLITS' ? removeSplitsFromTxnsAndUpdateUiState(txn.toJS()) : null,
            ['CANCEL', 'CLEAR SPLITS'],
            'backspace',
            false,
          );
        } else {
          removeSplitsFromTxnsAndUpdateUiState(txn.toJS());
        }
        return;
      }

      // show warning if the selected instance is a bank pending transaction
      if (isBankOwnedPending(txn) && isC2REnabledAccount(txn.accountId)) {
        dialogAlert('Pending Transaction', 'Pending transactions cannot be edited', undefined, ['ok']);
        return;
      }

      let { showSplitWindow } = uiState;
      if (openSplit || ((field === 'tags' || field === 'category') && transactionsUtils.isSplitTxn(txn))) {
        showSplitWindow = !showSplitWindow;
      }
      let updateObject = changeEditableField(fieldBeingClicked, { showSplitWindow });
      if (catSuggest) {
        updateObject = setTxnBeingEdited(setTxnReviewed(txn).set('coa', suggestTxn.coa), updateObject);
        updateObject.txnDirty = true;
      }
      const updatedUiState = setUIState(updateObject);
      if (catSuggest) {
        setTimeout(() => saveUpdatedTxnBeingEdited(updatedUiState), 50);
      }
      // CLICK IS IN  A NEW TX ROW
    } else {
      // if this is a scheduled transaction instance, and is not the "next" one, then we skip it, Quicken only
      if (isUnacceptedScheduledTxn(txn) && !isActionableScheduledInstance(txn, scheduledTransactionsById)) {
        return;
      }
      // show warning if the selected instance is a bank pending transaction
      if (isBankOwnedPending(txn) && isC2REnabledAccount(txn.accountId)) {
        dialogAlert('Pending Transaction', 'Pending transactions cannot be edited', undefined, ['ok']);
        return;
      }
      let showSplitWindow = false;
      if (((field === 'tags' || field === 'category') && transactionsUtils.isSplitTxn(txn))) {
        showSplitWindow = true;
      }
      let editTxn = txn;
      if (catSuggest) {
        editTxn = setTxnReviewed(txn).set('coa', suggestTxn.coa);
      }
      const updateStateObj = setTxnBeingEdited(editTxn, {
        txnDirty: Boolean(suggestTxn),
        currEditFieldName: showSplitWindow ? null : (fieldBeingClicked || uiState.currEditFieldName),
        simpleEdit: true,
        showSplitWindow,
      });

      if (uiState.txnBeingEdited) {
        maybeSaveTxnBeingEdited((confirmed) => {
          if (confirmed) {
            setUIState(updateStateObj);
          } else {
            log.log('SAVE ABORTED');
            forceUpdate();
          }
        });
      } else {
        const updatedUiState = setUIState(updateStateObj);
        if (updateStateObj.txnDirty) {
          setTimeout(() => saveUpdatedTxnBeingEdited(updatedUiState), 50);
        }
      }
    }
  };

  const regMenuExiting = () => {
    // this.setUIState({txnBeingEdited: null});
  };

  const columnIsVisible = (col) =>
    (props.regFieldsOrder.includes(col));

  const callbacks = {
    handleChange,
    handleFocus,
    regFieldKey,
    regFieldClick,
    handleMenu,
    hideModal,
    saveTransaction: txFormUpdate,
    saveDoc,
    delDocIds,
    headerClick,
    showNewTxn: showNewTxnMaybePrompt,
    onRowSelect,
    onCheckAll,
    regMenuExiting,
    openCloseSection,
    onSearchFilter,
    onAmountBlur,
    onFocusField,
    editSelectedTxns,
    deleteSelectedTxns,
    reviewSelectedTxns,
    matchSelectedTxns,
    refreshTxnList,
    bulkEditSave,
    scrollToTransaction,
    downloadFile,
    alterHeader,
    markTxnDirty,
    cancelEdit,
    maybeSaveTxnBeingEdited,
    detailsRefSaveFn,
    resetFocus,
    fastCategorize,
    reminderChange,
    actOnScheduledTxn,
    onApplyFilter,
    setCalendarDate,
  };

  useEffect(() => {
    if (props.addNewTransactionRef) {
      props.addNewTransactionRef(showNewTxnMaybePrompt);
    }
    clearAllFilters(props);

    return () => {
      log.debug('Transaction register will unmount...');
      if (searchBoxFilter) {
        setUIState({
          searchBoxFilter: '',
          selectedTxns: uiState.selectedTxns?.clear(),
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scheduledTransactionsLoadPending && !hideScheduledTransactionsInRegister && !autoGetScheduledTransactionRef.current) {
      autoGetScheduledTransactionRef.current = true;
      dispatch(scheduledTransactionsActions.getScheduledTransactions(undefined, { context: 'register' }));
    }
  }, [dispatch, scheduledTransactionsLoadPending, hideScheduledTransactionsInRegister]);

  const oldState = usePrevious(uiState);
  const prevTags = usePrevious(tags);
  const prevReminderSetting = usePrevious(props.reminderSetting);
  const prevAcctTxns = usePrevious(acctTxns);
  const prevAcctIds = usePrevious(accountIds);
  const prevDoubleAmounts = usePrevious(props.doubleAmounts);
  const prevRegFieldsOrder = usePrevious(props.regFieldsOrder);
  useEffect(() => {        
    let uiStateChanges: RegisterUIStateType = {};
    // If TAGS changed, we need to update the transaction being edited
    if (tags !== prevTags) {
      if (uiState.txnBeingEdited && uiState.simpleEdit && !uiState.showSplitWindow) {
        uiStateChanges = {
          ...uiStateChanges,
          txnBeingEdited: updateNewTags(uiState.txnBeingEdited),
        };
      }
    }
    if (props.reminderSetting !== prevReminderSetting) {
      if (props.reminderSetting !== uiState.reminderSetting) {
        // TODO: check reminderSetting is needed in uiState
        uiStateChanges = {
          ...uiStateChanges,
          reminderSetting: props.reminderSetting,
        };
      }
    }
    if (acctTxns && ((!uiState.scrollToId && acctTxns !== prevAcctTxns) || uiState.scrollToId !== oldState?.scrollToId)) {
      if (uiState.txnBeingEdited) {
        if (Number(uiState.txnBeingEdited.id ?? '0') !== 0) {
          // need a "find transaction by id"
          const txnList = findTxnsForIds(uiState.txnBeingEdited.id ? [uiState.txnBeingEdited.id] : []);
          if (txnList && txnList.length > 0) {
            const { txn } = txnList[0];
            uiStateChanges = {
              ...uiStateChanges,
              txnBeingEdited: txn,  // TODO figure out why this used to say uiState.txnBeingEdited
            };
          }
          log.log('TXN BEING EDITED NOW ', uiStateChanges.txnBeingEdited, uiState.txnDirty);
        } else if (uiState.showNewTxn) {
          let acct = oldState?.lastAccountEntered || firstBankAccount(accountIds);
          if (!accountIds.includes(acct)) {
            acct = accountIds.first();
          }
          const newTxn = makeNewTxn(acct);
          uiStateChanges = { ...uiStateChanges, lastAccountEntered: acct, txnBeingEdited: newTxn };
        }
      }
      // So if the user has made a change to the currently viewed register (accountNode) then we eliminate the
      // selection.  However, if they are just clicking between nodes, we do not
      let newSelectedTxns = uiState.selectedTxns;
      let newSelectedAll = uiState.allSelected;
      // if (this.props.accountNodeId === nextProps.accountNodeId) {
      // WE check the current selected transactions, and remove any that no longer exist in the acctTxns by ID
      newSelectedTxns = newSelectedTxns?.filter((selectedTxn) => Boolean(findTxnById(selectedTxn.id as string)?.txn));
      newSelectedAll = newSelectedAll && newSelectedTxns && newSelectedTxns.size > 0;
      // }
      uiStateChanges = {
        ...uiStateChanges,
        allSelected: newSelectedAll,
        selectedTxns: newSelectedTxns,
        showAttachmentColumnInHeader: txnListHasAttachment(acctTxns),
      };

      // We do this so behavior is such that if you are using a search filter, we do not restrict the number
      // of transactions to display (we use 300 as the upper limit).  However, we want to go back to the
      // prescribed preference limit when the filter is empty (but did change)
      const filterChanged = uiState.searchBoxFilter !== oldState?.searchBoxFilter;
      const filterChangedNonEmpty = filterChanged && uiState.searchBoxFilter && uiState.searchBoxFilter.length > 0;
      const scrollTxChanged = uiState.scrollToId && ((uiState.scrollToId !== oldState?.scrollToId) ||
        (uiState.scrollToId && (accountIds !== prevAcctIds)));      
      // Open sections on first time through
      // Here we are examining uiState changes
      if (acctTxns && (acctTxns.size > 0) && (
        // txnsFirstLoaded, if null, means ignore, but if it is a value, then re-init register if it is different from acctTxns
        // this is used to indicate a re-init is needed, but need to wait for acctTxns to change.  (see sort change)
        ((uiState.txnsFirstLoaded && (uiState.txnsFirstLoaded !== acctTxns)) ||
          uiState.accountIds !== oldState?.accountIds ||
          scrollTxChanged || filterChanged))) {

        const scrollId = uiState.scrollToId;
        const selectDestination = true;
        let addShortKeyToSections = null;
        if (scrollId) {
          const txnList = findTxnsForIds([scrollId], false);
          log.log('Transaction to scroll to is ', txnList);
          if (txnList && txnList.length > 0) {
            // save the shortKey to expand the register section where the transaction we are trying to view is located
            addShortKeyToSections = txnList[0].shortKey;
            uiStateChanges = {
              ...uiStateChanges,
              txnBeingEdited: selectDestination ? txnList[0].txn : null,
            };
          }
        }
        // initialize open sections for default tx viewing
        let sections = initShowTransactions(
          { ...props, acctTxns },
          filterChangedNonEmpty ? 300 : uiState.initNumberTxnsShown,
        );
        // open the seciton that contains the transaction we may be trying to scroll to
        if (addShortKeyToSections) {
          log.log('ADDING SHORT KEY ', addShortKeyToSections);
          sections = sections.add(addShortKeyToSections);
        }
        uiStateChanges =
        {
          ...uiStateChanges,
          txnsFirstLoaded: null,
          openSections: sections,
          showAttachmentColumnInHeader: txnListHasAttachment(acctTxns),
        };
      }
      // see if we need to reset regFields for preference change
      if (prevDoubleAmounts !== props.doubleAmounts) {
        uiStateChanges = {
          ...uiStateChanges,
          regEditableFields: newRegFields(props.doubleAmounts),
        };
      }
      // did register fields change?
      if (prevRegFieldsOrder !== props.regFieldsOrder) {
        uiStateChanges = { ...uiStateChanges, headerFields: props.regFieldsOrder };
      }
      // Weird case here, if the props changes make the current edit field invalid, we need to move it
      if (uiState.txnBeingEdited) {
        if (isSkipField(props, uiState.currEditFieldName, uiState.txnBeingEdited)) {
          const newField = getNextField({ ...props, uiState });
          log.log('CHANGING THE FIELD TO ', newField);
          uiStateChanges = { ...uiStateChanges, currEditFieldName: newField };
        }
      }
      // if changes applied, update UI state
      if (uiStateChanges && (Object.keys(uiStateChanges).length !== 0)) {
        setUIState({ ...uiStateChanges });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountIds,
    uiState,
    acctTxns,
    tags,
    oldState,
    prevTags,
    prevReminderSetting,
    prevAcctTxns,
    prevAcctIds,
    prevDoubleAmounts,
    prevRegFieldsOrder,
  ]);

  useEffect(() => {
    if (uiState.flashTx) {
      const animAmounts = document.querySelectorAll('.flashTx');
      animAmounts.forEach((item) => {
        addEventListenerOnce(item, 'animationend', () => {
          animationControl('flashTx');
        });
      });
      // if an animation isn't 'seen' within 1s, end it
      setTimeout(() => animationControl('flashTx'), 1000);
    }
    if (uiState.dataToDownload) {
      setUIState({ dataToDownload: null });
    }
    resetFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState.flashTx, uiState.dataToDownload]);

  return (
    <div
      className={classes.wrapper}
      role="presentation"
    >
      <TransactionPage
        {...otherProps}
        uiState={uiState}
        sortBy={sortBy}
        sortOrder={sortOrder}
        currEditFieldName={uiState.currEditFieldName || 'payee'}
        callbacks={callbacks}
        scrollToId={uiState.scrollToId as string}
        hideSearch={props.hideSearch}
        hideFooter={!columnIsVisible('reviewed')}
        headerFields={props.regFieldsOrder}
        showCurrencySymbol={props.showCurrencySymbol}
        reminderSetting={reminderSetting}
        showDebtErrorMessage={props.showDebtErrorMessage}
        zeroStateMessage={searchBoxFilter || filterObject ? 'No transactions match your search or filters.' : zeroStateMessage}
        calendarDate={calendarDate}
        allAccountIds={allAccountIds}
        acctTxns={acctTxns}
        // @ts-expect-error - FIXME: Type 'List<number>' is not assignable to type 'List<string>'
        accountIds={accountIds}
      />
    </div>
  );

});

// TODO: To check this shallowCompare as per below lifecycle method.
// shouldComponentUpdate(nextProps, nextState) {
//   return !this.deferUpdates && shallowCompare(this, nextProps, nextState);
// }

export default React.memo(TransactionRegister);
